import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import { Link } from "react-router-dom";
import VoucherCard from "../../components/Common/VoucherCard";
import VoucherDetailModal from "../../components/Common/VoucherDetailModal";
import RedeemVoucherModal from "../../components/Common/RedeemVoucherModal";
import SecondaryButton from "../../components/Common/SecondaryButton";
import { DataTable, IconAvatar, StatusBadge } from "../../components/Common/PageWidgets";
import EmptyState from "../../components/Common/EmptyState";
import ActivityDetailModal from "../../components/Common/ActivityDetailModal";
import whiteArrow from "../../assets/images/user-dashboard/white-right-arrow.svg";

//Import API
import endpoints from "../../api/endpoints";
import { get } from "../../api/axios";

//Utils
import { extractErrorMessage } from "../../utils/errorHandler";

//Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

import coinIcon from "../../assets/images/user-dashboard/voucher.svg";
import ticketIcon from "../../assets/images/user-dashboard/redeem.svg";
import starIcon from "../../assets/images/user-dashboard/star.svg";
import eyeIcon from "../../assets/images/support/eye.svg";


const TIERS = [
  { points: 3500, value: 25 },
  { points: 6000, value: 50 },
  { points: 10000, value: 100 },
];

const ACTIVITY_ICON = {
  referral_referrer: { icon: starIcon, tone: "teal" },
  referral_referee: { icon: starIcon, tone: "teal" },
  voucher_redemption: { icon: ticketIcon, tone: "orange" },
  manual_adjustment: { icon: coinIcon, tone: "blue" },
  passport: { icon: starIcon, tone: "purple" },
  streak: { icon: starIcon, tone: "teal" },
  game: { icon: starIcon, tone: "orange" },
};

const LEDGER_TYPE_LABELS = {
  transaction: "Transaction",
  referral_referrer: "Referral Bonus",
  referral_referee: "Referral Bonus",
  voucher_redemption: "Voucher Redemption",
  manual_adjustment: "Manual Adjustment",
  passport: "Passport Reward",
  streak: "Streak Reward",
  game: "Game Reward",
};

const SOURCE_TYPE_LABELS = {
  "App\\Models\\FinancialTransaction": "Financial Transaction",
  financial_transaction: "Financial Transaction",
  transaction: "Financial Transaction",
  voucher: "Voucher",
  "App\\Models\\Voucher": "Voucher",
  referral: "Referral",
  admin_manual_adjustment: "Admin Manual Adjustment",
  "App\\Models\\UserGameProgress": "Game Progress",
  game: "Game Progress",
  "App\\Models\\UserStreak": "Streak",
  streak: "Streak",
  "App\\Models\\UserPassportReward": "Passport",
  passport: "Passport",
};

const toTitleCase = (value) => (
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatSourceType = (value) => {
  if (!value) return "-";
  if (SOURCE_TYPE_LABELS[value]) return SOURCE_TYPE_LABELS[value];

  const className = String(value).split("\\").pop();
  return SOURCE_TYPE_LABELS[className] || toTitleCase(className);
};

const formatDateTime = (value) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${datePart} ${timePart}`;
};

const WalletPointsCard = ({ points, tiers, onRedeem }) => {
  document.title = "Wallet | Omaha Food Lovers";

  // Safety check for undefined tiers
  const safeTiers = tiers && tiers.length > 0 ? tiers : TIERS;

  // Find the next tier based on current points
  const nextTier = safeTiers.find(tier => tier.points > points) || safeTiers[safeTiers.length - 1];
  const currentTierIndex = safeTiers.findIndex(tier => tier.points > points) - 1;
  const currentTier = currentTierIndex >= 0 ? safeTiers[currentTierIndex] : null;

  // Calculate progress
  let percent = 0;
  let pointsToGo = 0;

  if (currentTier && nextTier) {
    // Progress from current tier to next tier
    const pointsInCurrentTier = points - currentTier.points;
    const pointsBetweenTiers = nextTier.points - currentTier.points;
    percent = Math.min(100, (pointsInCurrentTier / pointsBetweenTiers) * 100);
    pointsToGo = nextTier.points - points;
  } else if (!currentTier) {
    // Before first tier
    percent = Math.min(100, (points / nextTier.points) * 100);
    pointsToGo = nextTier.points - points;
  } else {
    // At or beyond last tier
    percent = 100;
    pointsToGo = 0;
  }

  return (
    <div className="wallet-points-card">
      <div className="wallet-points-card__top">
        <div>
          <p className="wallet-points-card__eyebrow">Reward</p>
          <h2 className="wallet-points-card__title">Available Points</h2>
        </div>
        <SecondaryButton label={<span><span className="hide-on-small">Add </span>Voucher</span>} variant="orange" icon={whiteArrow} onClick={onRedeem} />
      </div>

      <div className="wallet-points-card__value">
        <p>{points.toLocaleString()}</p>
        <span className="wallet-points-card__amount">{`= $${Math.floor(points / 100)}`}</span>
      </div>

      <div className="wallet-points-card__progress-row">
        <span className="wallet-points-card__progress-label">
          Next Reward: ${nextTier.value}
        </span>
        <span className="wallet-points-card__progress-value">
          {nextTier.points.toLocaleString()} pts
        </span>
      </div>
      <div className="wallet-progress-track">
        <div className="wallet-progress-fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="wallet-points-card__footnote">
        {pointsToGo > 0 ? `${pointsToGo.toLocaleString()} points to go` : 'Max tier reached'}
      </p>
    </div>
  );
};

WalletPointsCard.propTypes = {
  points: PropTypes.number.isRequired,
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      points: PropTypes.number.isRequired,
      value: PropTypes.number.isRequired,
    })
  ).isRequired,
  onRedeem: PropTypes.func,
};

WalletPointsCard.defaultProps = {
  onRedeem: () => {},
};

/* -------------------------------------------------------------------------
 * RewardTiersCard — static reference list of point tiers and their
 * matching voucher value; the tier the user has already reached is
 * emphasized.
 * ---------------------------------------------------------------------- */

const RewardTiersCard = ({ tiers, reachedPoints }) => {
  const hasReachedAnyTier = tiers.some(tier => reachedPoints >= tier.points);
  const hasMoreThanThreeTiers = tiers.length > 3;

  return (
    <div className="reward-tiers-card">
      <div className={`reward-tiers-card__header ${hasReachedAnyTier ? "reward-tiers-card__header--reached" : ""}`}>
        <p className="reward-tiers-card__eyebrow">Reward Tiers</p>
        <h2 className="reward-tiers-card__title">How Points turns into Vouchers</h2>
      </div>

      <div className={`reward-tiers-card__list ${hasMoreThanThreeTiers ? "reward-tiers-card__list--scrollable" : ""}`}>
        {tiers.map((tier) => {
          const reached = reachedPoints >= tier.points;
          return (
            <div
              key={tier.points}
              className={`reward-tiers-card__row ${
                reached ? "reward-tiers-card__row--reached" : ""
              }`}
            >
              <span>{tier.points.toLocaleString()} pts</span>
              <span>${tier.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

RewardTiersCard.propTypes = {
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      points: PropTypes.number.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  reachedPoints: PropTypes.number.isRequired,
};

/* -------------------------------------------------------------------------
 * Recent Activity Table Columns
 * ---------------------------------------------------------------------- */

const activityColumns = (onView) => [
  {
    key: "type",
    label: "Name",
    width: "2fr",
    render: (row) => {
      const { icon, tone } = ACTIVITY_ICON[row.type] || ACTIVITY_ICON.manual_adjustment;
      return (
        <span className="data-table__row-label">
          <IconAvatar icon={icon} tone={tone} size={32} />
          {LEDGER_TYPE_LABELS[row.type] || toTitleCase(row.type)}
        </span>
      );
    },
  },
  {
    key: "source_type",
    label: "Type",
    width: "1fr",
    render: (row) => row.sourceTypeLabel,
  },
  {
    key: "status",
    label: "Status",
    width: "1fr",
    render: (row) => <StatusBadge status={row.statusLabel} />,
  },
  {
    key: "points",
    label: "Points",
    width: "1fr",
    render: (row) => (
      <span style={{ color: row.status === 'credit' ? '#1D9C87' : '#d11f1f', fontWeight: 600 }}>
        {row.pointsDisplay}
      </span>
    ),
  },
  {
    key: "dateLabel",
    label: "Date",
    width: "1fr",
  },
  {
    key: "view",
    label: "",
    width: "64px",
    align: "center",
    render: (row) => (
      <button
        type="button"
        onClick={() => onView && onView(row.id)}
        style={{ border: "none", background: "none", cursor: "pointer", padding: 4 }}
        aria-label={`View ${row.typeLabel || "activity"}`}
      >
        <img src={eyeIcon} alt="" style={{ width: 18, height: 18 }} />
      </button>
    ),
  },
];

/* -------------------------------------------------------------------------
 * WalletPage
 * ---------------------------------------------------------------------- */

const WalletPage = ({
  points,
  nextRewardValue,
  nextRewardPoints,
  tiers,
  vouchers,
  activity,
  onRedeemPoints,
  onRedeemVoucher,
  onVoucherClick,
  onViewAllVouchers,
}) => {
  document.title = "Wallet | Omaha Food Lovers";
  const navigate = useNavigate();
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  const [redemptionTiers, setRedemptionTiers] = useState(tiers || TIERS);
  const [userVouchers, setUserVouchers] = useState(vouchers || []);
  const [userPoints, setUserPoints] = useState(points || 0);
  const [isLoadingVouchers, setIsLoadingVouchers] = useState(true);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Fetch redemption tiers from API
  const fetchRedemptionTiers = useCallback(async () => {
    try {
      const response = await get(endpoints.pointsRedemptionTiers);
      const apiTiers = response.data.data || [];
      
      // Transform API data to match expected format
      const transformedTiers = apiTiers
        .filter(tier => tier.is_active)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(tier => ({
          points: tier.points_required,
          value: tier.amount_cents / 100, // Convert cents to dollars
        }));
      
      setRedemptionTiers(transformedTiers);
    } catch (err) {
      console.error("Failed to fetch redemption tiers:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Use default tiers as fallback
      setRedemptionTiers(TIERS);
    }
  }, []);

  // Fetch redemption tiers on mount
  useEffect(() => {
    fetchRedemptionTiers();
  }, [fetchRedemptionTiers]);

  // Fetch vouchers from API
  const fetchVouchers = useCallback(async () => {
    setIsLoadingVouchers(true);
    try {
      const response = await get(endpoints.vouchers);
      const apiVouchers = response.data.data?.data || [];
      
      // Transform API data to match VoucherCard format
      const transformedVouchers = apiVouchers.map(voucher => {
        // Determine status based on API fields
        let status = "active";
        let statusDate = "";

        if (voucher.redeemed_at) {
          status = "redeemed";
          statusDate = voucher.redeemed_at;
        } else if (voucher.expires_at && new Date(voucher.expires_at) < new Date()) {
          status = "expired";
          statusDate = voucher.expires_at;
        } else if (voucher.expires_at) {
          status = "active";
          statusDate = voucher.expires_at;
        }

        return {
          id: voucher.id,
          amount: voucher.amount_cents ? voucher.amount_cents / 100 : voucher.amount || 0,
          status: status,
          statusDate: statusDate,
          restaurantName: voucher.vendor_listing?.title || voucher.restaurant?.name || voucher.restaurant_name || "Restaurant",
          expiresLabel: statusDate,
          expiresSubLabel: status === "active" ? "Valid" : status === "redeemed" ? "Redeemed" : "Expired",
          qrCodeUrl: voucher.qr_url || voucher.qr_code_url || "",
          voucherId: voucher.code || voucher.id?.toString(),
        };
      });
      
      setUserVouchers(transformedVouchers);
    } catch (err) {
      console.error("Failed to fetch vouchers:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Use empty array as fallback
      setUserVouchers([]);
    } finally {
      setIsLoadingVouchers(false);
    }
  }, []);

  // Fetch vouchers on mount
  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  // Fetch points from API
  const fetchPoints = useCallback(async () => {
    try {
      const response = await get(endpoints.pointsBalance);
      const apiPoints = response.data.data?.points || 0;
      setUserPoints(apiPoints);
    } catch (err) {
      console.error("Failed to fetch points:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Use 0 as fallback
      setUserPoints(0);
    }
  }, []);

  // Fetch points on mount
  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  // Fetch transactions from API
  const fetchTransactions = useCallback(async () => {
    setLoadingTransactions(true);
    try {
      const response = await get(`${endpoints.pointsLedger}?page=1`);
      
      if (response.data.success) {
        const transactionsData = response.data.data;
        const newTransactions = transactionsData.data || [];
        
        // Transform API response to match component's expected format
        const transformedTransactions = newTransactions.map((tx) => {
          const status = tx.status === "debit" ? "debit" : "credit";
          const points = Math.abs(Number(tx.points || 0));
          const sourceTypeLabel = formatSourceType(tx.source_type);

          return {
            id: tx.id,
            type: tx.type,
            typeLabel: LEDGER_TYPE_LABELS[tx.type] || toTitleCase(tx.type),
            status,
            statusLabel: status === "credit" ? "Credit" : "Debit",
            source_type: tx.source_type,
            sourceTypeLabel,
            points,
            pointsDisplay: `${status === "credit" ? "+" : "-"}${points.toLocaleString()}`,
            dateLabel: formatDateTime(tx.created_at),
            created_at: tx.created_at,
            meta: tx.meta,
          };
        });
        
        setTransactions(transformedTransactions);
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
      const errorMessage = extractErrorMessage(err);
      toastr.error(errorMessage);
      // Use empty array as fallback
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, []);

  // Fetch transactions on mount
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleViewActivity = (activityId) => {
    const act = transactions.find(a => a.id === activityId);
    if (act) {
      const { icon } = ACTIVITY_ICON[act.type] || ACTIVITY_ICON.manual_adjustment;

      // Generate user-friendly description
      let description = act.sourceTypeLabel;
      if (act.type === 'voucher_redemption' && act.status === 'debit') {
        const voucherCode = act.meta?.voucher_code || 'voucher';
        const tierId = act.meta?.tier_id;
        const tierInfo = tierId ? ` (Tier ${tierId})` : '';
        description = `${act.points.toLocaleString()} points deducted for creating voucher ${voucherCode}${tierInfo}`;
      } else if (act.type === 'manual_adjustment' && act.meta?.reason) {
        const adminEmail = act.meta?.adjusted_by_admin_email;
        const adminInfo = adminEmail ? ` by ${adminEmail}` : '';
        description = `${act.meta.reason}${adminInfo}`;
      } else if (act.type === 'referral_referrer') {
        description = `${act.points.toLocaleString()} bonus points earned for referring a friend`;
      }

      const modalActivityData = {
        icon,
        statusLabel: act.statusLabel,
        title: act.typeLabel,
        description: description,
        type: act.sourceTypeLabel,
        event_label: act.statusLabel,
        date: act.dateLabel,
        points: act.status === 'debit' ? -act.points : act.points,
        pointsTone: act.status === 'credit' ? 'green' : 'red',
      };
      setSelectedActivity(modalActivityData);
      setIsActivityModalOpen(true);
    }
  };

  const handleCloseActivityModal = () => {
    setIsActivityModalOpen(false);
    setSelectedActivity(null);
  };

  // Redeem flow state
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);

  const handleCloseVoucherModal = () => {
    setIsVoucherModalOpen(false);
    setSelectedVoucherId(null);
  };

  const handleRestaurantClick = () => {
    // Navigate to restaurant detail or handle restaurant click
    handleCloseVoucherModal();
  };

  // Redeem flow handlers
  const handleStartRedeem = () => {
    setIsRedeemModalOpen(true);
  };

  const handleVoucherCardRedeem = (voucherId) => {
    setSelectedVoucherId(voucherId);
    setIsVoucherModalOpen(true);
  };

  const handleCloseRedeemModal = () => {
    setIsRedeemModalOpen(false);
  };

  const handleVoucherCreated = () => {
    // Refresh vouchers after creation
    fetchVouchers();
    // Refresh points after redemption
    fetchPoints();
    // Refresh transactions after voucher creation
    fetchTransactions();
  };

  const handleNavigateToWallet = () => {
    navigate("/wallet");
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="g-3">
          <Col xl={8}>
            <WalletPointsCard
              points={userPoints}
              tiers={redemptionTiers}
              onRedeem={handleStartRedeem}
            />
          </Col>
          <Col xl={4}>
            <RewardTiersCard tiers={redemptionTiers} reachedPoints={userPoints} />
          </Col>
        </Row>
       <div className="wallet-section-header">
          <h2 className="wallet-section-header__title">Active Vouchers</h2>
          <Link to="/vouchers">
            <SecondaryButton label="View All" variant="orange" icon={whiteArrow} />
          </Link>
        </div>

        <div className="vouchers-grid vouchers-grid--preview">
          {isLoadingVouchers ? (
            [1, 2, 3].map((index) => (
              <div key={index} className="voucher-card voucher-card--skeleton">
                <div className="voucher-card__stub">
                  <div className="skeleton-shimmer" />
                </div>
                <div className="voucher-card__body">
                  <div className="voucher-card__top">
                    <div className="voucher-card__amount skeleton-shimmer" />
                    <div className="voucher-card__pill skeleton-shimmer" />
                  </div>
                  <div className="voucher-card__bottom">
                    <div className="voucher-card__valid-at">
                      <div className="voucher-card__valid-label skeleton-shimmer" />
                      <div className="voucher-card__restaurant skeleton-shimmer" />
                    </div>
                    <div className="voucher-card__button skeleton-shimmer" />
                  </div>
                </div>
              </div>
            ))
          ) : userVouchers && userVouchers.length > 0 ? (
            userVouchers.filter(voucher => voucher.status === 'active').slice(0, 8).map((voucher) => (
              <VoucherCard
                key={voucher.id}
                amount={voucher.amount}
                status={voucher.status}
                statusDate={voucher.statusDate}
                restaurantName={voucher.restaurantName}
                voucherCode={voucher.voucherId}
                onRedeem={() => handleVoucherCardRedeem(voucher.id)}
              />
            ))
          ) : (
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "center" }}>
              <EmptyState
                title="No vouchers yet"
                message="Redeem your points to get vouchers for dining at partner restaurants!"
              />
            </div>
          )}
        </div>
      

     

        <div className="wallet-section-header">
          <h2 className="wallet-section-header__title">Points Activity</h2>
          <div className="wallet-section-header__right">
            <span className="wallet-section-header__note">Appears within 24 hrs</span>
            <Link to="/points-activity">
              <SecondaryButton label="View All" variant="orange" icon={whiteArrow} />
            </Link>
          </div>
        </div>

        <div className="activity-list">
          {loadingTransactions ? (
            <DataTable 
              columns={activityColumns(handleViewActivity)} 
              rows={[]} 
              getRowKey={(row) => row.id}
              loading={true}
            />
          ) : transactions && transactions.length > 0 ? (
            <DataTable 
              columns={activityColumns(handleViewActivity)} 
              rows={transactions.slice(0, 10)} 
              getRowKey={(row) => row.id}
            />
          ) : (
            <EmptyState
              title="No recent activity yet"
              message="Your recent dining activity and transactions will appear here."
            />
          )}
        </div>
      </Container>

      {selectedVoucherId && (
        <VoucherDetailModal
          voucherId={selectedVoucherId}
          isOpen={isVoucherModalOpen}
          onClose={handleCloseVoucherModal}
          onRestaurantClick={handleRestaurantClick}
        />
      )}

      <RedeemVoucherModal
        isOpen={isRedeemModalOpen}
        onClose={handleCloseRedeemModal}
        userPoints={userPoints}
        onVoucherCreated={handleVoucherCreated}
        onNavigateToWallet={handleNavigateToWallet}
      />

      <ActivityDetailModal
        activity={selectedActivity}
        isOpen={isActivityModalOpen}
        onClose={handleCloseActivityModal}
      />
    </div>
  );
};

WalletPage.propTypes = {
  points: PropTypes.number.isRequired,
  nextRewardValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  nextRewardPoints: PropTypes.number.isRequired,
  tiers: PropTypes.array.isRequired,
  vouchers: PropTypes.array.isRequired,
  activity: PropTypes.array.isRequired,
  onRedeemPoints: PropTypes.func,
  onRedeemVoucher: PropTypes.func,
  onVoucherClick: PropTypes.func,
  onViewAllVouchers: PropTypes.func,
};

WalletPage.defaultProps = {
  points: 0,
  nextRewardValue: 0,
  nextRewardPoints: 0,
  tiers: [],
  vouchers: [],
  activity: [],
  onRedeemPoints: () => {},
  onRedeemVoucher: () => {},
  onVoucherClick: () => {},
  onViewAllVouchers: () => {},
};

export default WalletPage;
